import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./IntelligentAutomation.css";
import Testimonial from "../../../Testimonial/Testimonial";

const IntelligentAutomation = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>Intelligent Automation | Advanced Tech Stack & Tools</title>
        <meta
          name="description"
          content="Discover our intelligent automation solutions powered by cutting-edge technologies including AI, blockchain, IoT, and web/mobile development frameworks."
        />
        <meta
          name="keywords"
          content="intelligent automation, AI automation, artificial intelligence, RPA, robotic process automation, business process automation, cognitive automation, machine learning, deep learning, neural networks, natural language processing, computer vision, predictive analytics, smart automation, digital transformation, workflow automation, process optimization, hyperautomation, AI-powered solutions, intelligent systems, automation tools, blockchain technology, IoT solutions, web development frameworks, mobile app development, tech stack, Angular development, React.js, Node.js, Python automation, Java automation, cloud automation, enterprise automation, chatbot development, virtual assistants, automated decision making, data automation, intelligent workflows, self-learning systems, automation consulting, automation services, AI integration, business automation, industry 4.0, smart technology, digital workforce, automated solutions, AI algorithms, automation software, cognitive computing, intelligent document processing, task automation, AI development, automation platform, no-code automation, low-code automation"
        />
      </Helmet>


          <div className="ia-page-container">
      {/* Hero Section */}
      <section className="ia-hero-section">
        <div className="ia-hero-content">
          <h1 className="ia-hero-title">
            Intelligent Automation
            <span className="ia-title-underline"></span>
          </h1>
          <p className="ia-hero-subtitle">
            Transform your business processes with AI-powered automation solutions that learn, adapt, and evolve.
          </p>
          <a href="/contact-us"><button className="ia-cta-button">Explore Solutions</button></a>
        </div>
        <div className="ia-hero-visual">
          <div className="ia-animated-circle ia-circle-1"></div>
          <div className="ia-animated-circle ia-circle-2"></div>
          <div className="ia-animated-circle ia-circle-3"></div>
          <div className="ia-core-element">
            <div className="ia-inner-core"></div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
<section className="ia-benefits-section">
  <h2 className="ia-section-title">Why Choose Our Intelligent Automation?</h2>
  <p className="ia-section-subtitle">Leverage cutting-edge AI technology to transform your business operations and gain competitive advantage</p>
  
  <div className="ia-benefits-grid">
    <div className="ia-benefit-card">
      <div className="ia-benefit-icon">
        <div className="ia-icon-brain"></div>
      </div>
      <h3>AI-Powered Decision Making</h3>
      <p>Our systems analyze complex data patterns to make intelligent, real-time decisions, significantly reducing human intervention while improving accuracy.</p>
      <div className="ia-benefit-hover-content">
        <ul>
          <li>Real-time data processing</li>
          <li>Predictive analytics integration</li>
          <li>Reduced decision latency</li>
        </ul>
      </div>
    </div>
    
    <div className="ia-benefit-card">
      <div className="ia-benefit-icon">
        <div className="ia-icon-growth"></div>
      </div>
      <h3>Enhanced Efficiency</h3>
      <p>Automate complex workflows and processes, achieving up to 70% reduction in operational costs while improving throughput and quality.</p>
      <div className="ia-benefit-hover-content">
        <ul>
          <li>Process optimization</li>
          <li>Resource utilization tracking</li>
          <li>Continuous improvement metrics</li>
        </ul>
      </div>
    </div>
    
    <div className="ia-benefit-card">
      <div className="ia-benefit-icon">
        <div className="ia-icon-adaptive"></div>
      </div>
      <h3>Adaptive Learning</h3>
      <p>Our solutions continuously learn from data patterns and user interactions to improve over time, becoming more effective with each iteration.</p>
      <div className="ia-benefit-hover-content">
        <ul>
          <li>Machine learning algorithms</li>
          <li>Behavioral pattern recognition</li>
          <li>Self-optimizing workflows</li>
        </ul>
      </div>
    </div>
    
    <div className="ia-benefit-card">
      <div className="ia-benefit-icon">
        <div className="ia-icon-integration"></div>
      </div>
      <h3>Seamless Integration</h3>
      <p>Connect with your existing systems and workflows through our flexible API architecture and integration framework.</p>
      <div className="ia-benefit-hover-content">
        <ul>
          <li>API-first architecture</li>
          <li>Legacy system compatibility</li>
          <li>Custom integration solutions</li>
        </ul>
      </div>
    </div>
    
    <div className="ia-benefit-card">
      <div className="ia-benefit-icon">
        <div className="ia-icon-scalability"></div>
      </div>
      <h3>Enterprise Scalability</h3>
      <p>From small departments to global enterprises, our solutions scale to meet your growing business needs without performance degradation.</p>
      <div className="ia-benefit-hover-content">
        <ul>
          <li>Cloud-native architecture</li>
          <li>Elastic resource allocation</li>
          <li>Global deployment options</li>
        </ul>
      </div>
    </div>
    
    <div className="ia-benefit-card">
      <div className="ia-benefit-icon">
        <div className="ia-icon-security"></div>
      </div>
      <h3>Advanced Security</h3>
      <p>Protect your sensitive data with enterprise-grade security features including encryption, access controls, and compliance frameworks.</p>
      <div className="ia-benefit-hover-content">
        <ul>
          <li>End-to-end encryption</li>
          <li>Role-based access control</li>
          <li>GDPR & HIPAA compliance</li>
        </ul>
      </div>
    </div>
  </div>
  
  <div className="ia-benefits-connector">
    <div className="ia-connector-line"></div>
    <div className="ia-connector-dots">
      <div className="ia-connector-dot"></div>
      <div className="ia-connector-dot"></div>
      <div className="ia-connector-dot"></div>
    </div>
  </div>
</section>

      {/* Solutions Section */}
      <section className="ia-solutions-section">
        <h2 className="ia-section-title">Our Intelligent Automation Solutions</h2>
        
        <div className="ia-solution-tabs">
          <div className="ia-tab-content">
            <div className="ia-tab-pane active">
              <div className="ia-solution-content">
                <div className="ia-solution-text">
                  <h3>Cognitive Process Automation</h3>
                  <p>Go beyond traditional automation with systems that understand, learn, and adapt. Our cognitive automation solutions handle unstructured data and make context-aware decisions.</p>
                  <ul className="ia-solution-features">
                    <li>Natural Language Processing</li>
                    <li>Pattern Recognition</li>
                    <li>Predictive Analytics</li>
                    <li>Continuous Learning Algorithms</li>
                  </ul>
                </div>
                <div className="ia-solution-visual">
                  <div className="ia-cognitive-animation">
                    <div className="ia-cognitive-node ia-node-1"></div>
                    <div className="ia-cognitive-node ia-node-2"></div>
                    <div className="ia-cognitive-node ia-node-3"></div>
                    <div className="ia-cognitive-center"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="ia-usecases-section">
        <h2 className="ia-section-title">Intelligent Automation In Action</h2>
        
        <div className="ia-usecase-container">
          <div className="ia-usecase">
            <div className="ia-usecase-dot"></div>
            <div className="ia-usecase-content">
              <h3>Customer Service Automation</h3>
              <p>AI-powered chatbots and virtual assistants that understand customer intent and provide personalized solutions.</p>
            </div>
          </div>
          
          <div className="ia-usecase">
            <div className="ia-usecase-dot"></div>
            <div className="ia-usecase-content">
              <h3>Intelligent Data Processing</h3>
              <p>Automated extraction, classification, and processing of documents with cognitive capabilities.</p>
            </div>
          </div>
          
          <div className="ia-usecase">
            <div className="ia-usecase-dot"></div>
            <div className="ia-usecase-content">
              <h3>Predictive Maintenance</h3>
              <p>AI systems that predict equipment failures and schedule maintenance before issues occur.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ia-cta-section">
        <div className="ia-cta-background">
          <div className="ia-cta-content">
            <h2>Ready to Transform Your Business?</h2>
            <p>Schedule a consultation with our experts to discover how intelligent automation can revolutionize your operations.</p>
            <a href="/contact-us"><button className="ia-cta-button ia-cta-button-secondary">Get Started Today</button></a>
          </div>
        </div>
      </section>
    </div>


      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntelligentAutomation;
